/**
 * useOnChainAvatars — other people's avatars, read from the `properavatar`
 * contract and verified against current NFT ownership.
 *
 * Deliberately fail-quiet: no avatar is the normal case, and a chat list must
 * never wait on AtomicAssets or IPFS to render.
 */

import { useEffect, useMemo, useState } from 'react'
import { resolveAvatars } from '@properwallet/chain-eos'

export function useOnChainAvatars(accounts: string[]): Record<string, string> {
  const [urls, setUrls] = useState<Record<string, string>>({})

  // The array identity changes on every render of the caller; the contents do not.
  const key = useMemo(() => [...new Set(accounts)].sort().join(','), [accounts])

  useEffect(() => {
    const names = key.split(',').filter(Boolean)
    if (names.length === 0) return
    let cancelled = false
    void resolveAvatars(names).then(map => {
      if (cancelled) return
      const next: Record<string, string> = {}
      for (const [account, avatar] of map) {
        // Videos are skipped: an avatar is a small static circle.
        if (avatar.mediaType !== 'video') next[account] = avatar.mediaUrl
      }
      setUrls(prev => ({ ...prev, ...next }))
    })
    return () => {
      cancelled = true
    }
  }, [key])

  return urls
}
